import { Group, GroupUser } from "@/api/groups";
import { config } from "@/config";
import type { Relationship } from "@cohesive/api-client";
import {
  FlowSession,
  IntegrationConnection,
  Organization,
  OrganizationUser,
  Skill,
  UserOnboardingExperienceLevel,
} from "@cohesive/api-client";
import mixpanel from "mixpanel-browser";

// New enum for analytics event names
enum AnalyticsEvent {
  APP_INSTALLED = "App Installed",
  AUDIO_RECORDING_STARTED = "Audio Recording Started",
  AUDIO_RECORDING_STOPPED = "Audio Recording Stopped",
  RELATIONSHIP_CREATED = "Relationship Created",
  RELATIONSHIP_DELETED = "Relationship Deleted",
  RELATIONSHIP_UPDATED = "Relationship Updated",
  ASSISTANT_CREATED = "Assistant Created",
  ASSISTANT_DELETED = "Assistant Deleted",
  ASSISTANT_EDITED = "Assistant Edited",
  ASSISTANT_OPENED = "Assistant Opened",
  CHAT_DELETED = "Chat Deleted",
  DESKTOP_APP_DOWNLOAD_CLICKED = "Desktop App Download Clicked",
  CHAT_RENAMED = "Chat Renamed",
  FILE_ADDED_TO_FOLDER = "File Added to Folder",
  FOLDER_CREATED = "Folder Created",
  FOLDER_DELETED = "Folder Deleted",
  FOLDER_INFO_UPDATED = "Folder Info Updated",
  FOLDER_OPENED = "Folder Opened",
  GROUP_CREATED = "Group Created",
  GROUP_DELETED = "Group Deleted",
  GROUP_MEMBER_ADDED = "Group Member Added",
  GROUP_MEMBER_REMOVED = "Group Member Removed",
  GROUP_OPENED = "Group Opened",
  GROUP_UPDATED = "Group Updated",
  LIGHT_DARK_MODE_TOGGLE = "Light / Dark Mode Toggle",
  LOGIN = "Login",
  LOGOUT = "Logout",
  MESSAGE_COPIED = "Message Copied",
  MESSAGE_FEEDBACK_SUBMITTED = "Message Feedback Submitted",
  TOOL_FEEDBACK_SUBMITTED = "Tool Feedback Submitted",
  MESSAGE_SENT = "Message Sent",
  MESSAGE_THUMBS_DOWN = "Message Disliked",
  MESSAGE_THUMBS_UP = "Message Liked",
  NEW_CHAT_CREATED = "New Chat Created",
  FLOATING_CHAT_OPENED = "Floating Chat Opened",
  FLOATING_CHAT_OPENED_AS_TAB = "Floating Chat Opened As Tab",
  FLOATING_CHAT_MINIMIZED = "Floating Chat Minimized",
  ORGANIZATION_CONNECTION_AUTHORIZED = "Organization Connection Authorized",
  ORGANIZATION_CONNECTION_DELETED = "Organization Connection Deleted",
  ORGANIZATION_CREATED = "Organization Created",
  ORGANIZATION_USERS_OPENED = "Organization Users Opened",
  TEMPLATE_SIGNUP_STARTED = "Template Signup Started",
  TEMPLATE_PROMPT_SHOWN = "Template Prompt Shown",
  TEMPLATE_PROMPT_DISMISSED = "Template Prompt Dismissed",
  TEMPLATE_COPY_COMPLETED = "Template Copy Completed",
  TEMPLATE_COPY_FAILED = "Template Copy Failed",
  SLACK_ORGANIZATION_CONNECTED = "Slack Organization Connected",
  SLACK_USER_LINKED = "Slack User Linked",
  USER_CONNECTION_AUTHORIZED = "User Connection Authorized",
  USER_CONNECTION_DELETED = "User Connection Delete",
  USER_CONNECTION_PROMPT_CLICKED = "User Connection Prompt Clicked",
  DISCOVERABLE_CONNECTION_ACCESS_REQUESTED = "Discoverable Connection Access Requested",
  USER_DELETED = "User Deleted",
  USER_INVITED = "User Invited",
  USER_PROFILE_EDITED = "User Profile Edited",
  USER_ROLE_EDITED = "User Role Edited",
  SKILL_CREATED = "Skill Created",
  SKILL_DELETED = "Skill Deleted",
  SKILL_EDITED = "Skill Edited",
  FOLLOW_UP_CLICKED = "Follow Up Clicked",
  SAVE_INTEGRATION_CONNECTION_INSTRUCTIONS = "Save Integration Connection Instructions",
  INBOX_ITEM_CLICKED = "Inbox Item Clicked",
  INBOX_ITEM_ARCHIVED = "Inbox Item Archived",
  INBOX_ITEM_STARRED = "Inbox Item Starred",
  ONBOARDING_COMPLETED = "Onboarding Completed",
  ONBOARDING_EXPERIENCE_LEVEL_SELECTED = "Onboarding Experience Level Selected",
  INTEGRATION_CONNECTION_RECORD_SELECTED = "Integration Connection Record Selected",
  SKILL_SELECTED = "Skill Selected",
  // Billing Events
  BILLING_PLAN_SELECTED = "Billing Plan Selected",
  BILLING_PLAN_CONFIGURED = "Billing Plan Configured",
  BILLING_ENTERPRISE_INQUIRY_CLICKED = "Billing Enterprise Inquiry Clicked",
  ENTERPRISE_INQUIRY_SUBMITTED = "Enterprise Inquiry Submitted",
  BILLING_CHECKOUT_SUBMITTED = "Billing Checkout Submitted",
  BILLING_CHECKOUT_COMPLETED = "Billing Checkout Completed",
  BILLING_CHECKOUT_FAILED = "Billing Checkout Failed",
  BILLING_SUBSCRIPTION_CANCELLED = "Billing Subscription Cancelled",
  BILLING_SUBSCRIPTION_REACTIVATED = "Billing Subscription Reactivated",
  BILLING_PAYMENT_METHOD_UPDATE_STARTED = "Billing Payment Method Update Started",
  BILLING_BUDGET_UPDATED = "Billing Budget Updated",
  BILLING_EMAIL_UPDATED = "Billing Email Updated",
  BILLING_INVOICE_DOWNLOADED = "Billing Invoice Downloaded",
  BILLING_INVOICE_VIEWED = "Billing Invoice Viewed",
  BILLING_TRIAL_PAYMENT_PROMPT_CLICKED = "Billing Trial Payment Prompt Clicked",
  BILLING_USAGE_LIMIT_ACTION_CLICKED = "Billing Usage Limit Action Clicked",
  BILLING_MANAGE_CLICKED = "Billing Manage Clicked",
  BILLING_PLAN_CHANGE_CLICKED = "Billing Plan Change Clicked",
  // Onboarding Events
  ONBOARDING_EMAIL_CONNECTED = "Onboarding Email Connected",
  ONBOARDING_APPS_SELECTED = "Onboarding Apps Selected",
  ONBOARDING_BOOK_DEMO_CLICKED = "Onboarding Book Demo Clicked",
  ONBOARDING_SKIP_EMAIL = "Onboarding Skip Email",
  // Global Search Events
  GLOBAL_SEARCH_OPENED = "Global Search Opened",
  GLOBAL_SEARCH_QUERY_SUBMITTED = "Global Search Query Submitted",
  GLOBAL_SEARCH_RESULT_SELECTED = "Global Search Result Selected",
  GLOBAL_SEARCH_CLOSED = "Global Search Closed",
}

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Utility function to convert a string to snake_case
const snakeCase = (str: string): string => {
  return str
    .replace(/([A-Z])/g, "_$1") // Add underscores before capital letters
    .toLowerCase(); // Convert to lowercase
};

// Function to send analytics events
const sendAnalyticsEvent = (
  eventName: AnalyticsEvent,
  properties: Record<string, any>,
): void => {
  if (!config.mixpanel.enabled) {
    return;
  }

  const formattedProperties: Record<string, any> = {};
  for (const [key, value] of Object.entries(properties)) {
    formattedProperties[snakeCase(key)] = value; // Convert keys to snake_case
  }

  mixpanel.track(eventName, formattedProperties);
};

// Event Tracking Functions
export const trackDesktopAppDownloadClicked = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.DESKTOP_APP_DOWNLOAD_CLICKED, {});
};

export const trackIntegrationConnectionRecordSelected = ({
  entity,
}: {
  entity: {
    platform: string;
    type: string;
    id: string;
    name: string;
  };
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.INTEGRATION_CONNECTION_RECORD_SELECTED, {
    entity,
  });
};

export const trackSkillSelected = ({ skill }: { skill: Skill }): void => {
  sendAnalyticsEvent(AnalyticsEvent.SKILL_SELECTED, { skill });
};

export const trackAudioRecordingStarted = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.AUDIO_RECORDING_STARTED, {});
};

export const trackAudioRecordingStopped = ({
  durationSeconds,
  wasAutoStopped = false,
}: {
  durationSeconds: number;
  wasAutoStopped?: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.AUDIO_RECORDING_STOPPED, {
    durationSeconds,
    wasAutoStopped,
  });
};

export const trackRelationshipCreated = ({
  relationship,
  resource,
  subject,
}: {
  relationship: Relationship;
  resource?: OrganizationUser | Group | Organization;
  subject?: OrganizationUser | Group | Organization;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.RELATIONSHIP_CREATED, {
    relationship,
    resource,
    subject,
  });
};

export const trackRelationshipDeleted = ({
  relationship,
  resource,
  subject,
}: {
  relationship: Relationship;
  resource?: OrganizationUser | Group | Organization;
  subject?: OrganizationUser | Group | Organization;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.RELATIONSHIP_DELETED, {
    relationship,
    resource,
    subject,
  });
};

export const trackRelationshipUpdated = ({
  relationship,
  resource,
  subject,
}: {
  relationship: Relationship;
  resource?: OrganizationUser | Group | Organization;
  subject?: OrganizationUser | Group | Organization;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.RELATIONSHIP_UPDATED, {
    relationship,
    resource,
    subject,
  });
};

export const trackChatMessageSent = ({
  content,
  fileRecordIds,
}: {
  content?: string;
  fileRecordIds?: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_SENT, {
    content,
    fileRecordIds,
  });
};

export const trackGroupCreated = ({ group }: { group: Group }): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_CREATED, { group });
};

export const trackGroupDeleted = ({ group }: { group: Group }): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_DELETED, { group });
};

export const trackGroupMemberAdded = ({
  group,
  member,
}: {
  group: Group;
  member: OrganizationUser;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_MEMBER_ADDED, { group, member });
};

export const trackGroupMemberRemoved = ({
  group,
  member,
}: {
  group: Group;
  member: GroupUser;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_MEMBER_REMOVED, { group, member });
};

export const trackGroupOpened = ({ group }: { group: Group }): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_OPENED, { group });
};

export const trackGroupUpdated = ({ group }: { group: Group }): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_UPDATED, { group });
};

export const trackHomeMessageSent = ({
  content,
  fileRecordIds,
}: {
  content?: string;
  fileRecordIds?: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_SENT, {
    content,
    fileRecordIds,
  });
};

export const trackFloatingChatOpened = ({
  source,
  workspaceScoped,
}: {
  source: string;
  workspaceScoped: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FLOATING_CHAT_OPENED, {
    source,
    workspaceScoped,
  });
};

export const trackFloatingChatOpenedAsTab = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.FLOATING_CHAT_OPENED_AS_TAB, {});
};

export const trackFloatingChatMinimized = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.FLOATING_CHAT_MINIMIZED, {});
};

export const trackLightDarkModeToggle = ({
  mode,
}: {
  mode: "light" | "dark";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.LIGHT_DARK_MODE_TOGGLE, { mode });
};

export const trackLogin = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.LOGIN, {});

  window.dataLayer.push({
    event: "login",
  });
};

export const trackLogout = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.LOGOUT, {});
};

export const trackMessageCopied = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_COPIED, {});
};

export const trackMessageFeedbackSubmitted = ({
  feedback,
  messageId,
  messageContent,
}: {
  feedback: string;
  messageId: string;
  messageContent: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_FEEDBACK_SUBMITTED, {
    feedback,
    messageId,
    messageContent,
  });
};

export const trackToolFeedbackSubmitted = ({
  feedback,
  toolName,
}: {
  feedback: string;
  toolName: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TOOL_FEEDBACK_SUBMITTED, {
    feedback,
    toolName,
  });
};

export const trackMessageThumbsDown = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_THUMBS_DOWN, {});
};

export const trackMessageThumbsUp = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_THUMBS_UP, {});
};

export const trackNewChatCreated = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.NEW_CHAT_CREATED, {});
};

export const trackOrganizationConnectionAuthorized = ({
  connection,
}: {
  connection: IntegrationConnection;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ORGANIZATION_CONNECTION_AUTHORIZED, {
    connection,
  });
};

export const trackOrganizationConnectionDeleted = ({
  connection,
}: {
  connection: IntegrationConnection;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ORGANIZATION_CONNECTION_DELETED, {
    connection,
  });
};

export const trackOrganizationCreated = ({
  organization,
  signupSource,
}: {
  organization: Organization;
  signupSource?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ORGANIZATION_CREATED, {
    organization,
    ...(signupSource ? { signupSource } : {}),
  });

  window.dataLayer.push({
    event: "sign_up",
  });
};

export const trackTemplateSignupStarted = ({
  kind,
  cta,
  id,
}: {
  kind: string;
  cta: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_SIGNUP_STARTED, { kind, cta, id });
};

export const trackTemplatePromptShown = ({
  kind,
  id,
}: {
  kind: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_PROMPT_SHOWN, { kind, id });
};

export const trackTemplatePromptDismissed = ({
  kind,
  id,
}: {
  kind: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_PROMPT_DISMISSED, { kind, id });
};

export const trackTemplateCopyCompleted = ({
  kind,
  id,
}: {
  kind: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_COPY_COMPLETED, { kind, id });
};

export const trackTemplateCopyFailed = ({
  kind,
  id,
  reason,
}: {
  kind: string;
  id: string;
  reason: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_COPY_FAILED, { kind, id, reason });
};

export const trackSkillCreated = ({ skill }: { skill: Skill }): void => {
  sendAnalyticsEvent(AnalyticsEvent.SKILL_CREATED, { skill });
};

export const trackSkillDeleted = ({ skill }: { skill: Skill }): void => {
  sendAnalyticsEvent(AnalyticsEvent.SKILL_DELETED, { skill });
};

export const trackSkillEdited = ({ skill }: { skill: Skill }): void => {
  sendAnalyticsEvent(AnalyticsEvent.SKILL_EDITED, { skill });
};

export const trackSlackOrganizationConnected = ({
  slackTeamId,
  slackAppId,
}: {
  slackTeamId: string;
  slackAppId: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SLACK_ORGANIZATION_CONNECTED, {
    slackTeamId,
    slackAppId,
  });
};

export const trackSlackUserLinked = ({
  slackTeamId,
  slackAppId,
}: {
  slackTeamId: string;
  slackAppId: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SLACK_USER_LINKED, {
    slackTeamId,
    slackAppId,
  });
};

export const trackUserConnectionAuthorized = ({
  connection,
}: {
  connection: IntegrationConnection;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_CONNECTION_AUTHORIZED, {
    connection,
  });
};

export const trackUserConnectionDeleted = ({
  connection,
}: {
  connection: IntegrationConnection;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_CONNECTION_DELETED, {
    connection,
  });
};

export const trackUserConnectionPromptClicked = ({
  platform,
}: {
  platform: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_CONNECTION_PROMPT_CLICKED, {
    platform,
  });
};

export const trackDiscoverableConnectionAccessRequested = ({
  integration,
  useCase,
}: {
  integration: {
    id: string;
    name: string;
    platform: string;
  };
  useCase?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.DISCOVERABLE_CONNECTION_ACCESS_REQUESTED, {
    integration,
    useCase: useCase || null,
  });
};

export const trackUserDeleted = ({
  user,
}: {
  user: OrganizationUser;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_DELETED, { user });
};

export const trackUserInvited = ({
  targetUserEmail,
  targetUserRoles,
}: {
  targetUserEmail: string;
  targetUserRoles: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_INVITED, {
    target_user_email: targetUserEmail,
    target_user_roles: targetUserRoles,
  });
};

export const trackUserProfileEdited = (
  editedValues: Record<string, any>,
): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_PROFILE_EDITED, {
    edited_values: editedValues,
  });
};

export const trackUserRoleEdited = ({
  user,
  oldRoles,
  newRoles,
}: {
  user: OrganizationUser;
  oldRoles: string[];
  newRoles: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_ROLE_EDITED, {
    user,
    oldRoles,
    newRoles,
  });
};

export const trackFollowUpClicked = ({
  title,
  content,
  flowSession,
}: {
  title: string;
  content?: string;
  flowSession: FlowSession;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FOLLOW_UP_CLICKED, {
    title,
    content,
    flowSession,
  });
};

export const trackSaveIntegrationConnectionInstructions = ({
  integration_connection,
  instructions,
}: {
  integration_connection: IntegrationConnection;
  instructions: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SAVE_INTEGRATION_CONNECTION_INSTRUCTIONS, {
    integration_connection,
    instructions,
  });
};

export const trackAppInstalled = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.APP_INSTALLED, {});
};

export const trackOnboardingCompleted = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_COMPLETED, {});
};

export const trackOnboardingExperienceLevelSelected = ({
  experienceLevel,
}: {
  experienceLevel: UserOnboardingExperienceLevel;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_EXPERIENCE_LEVEL_SELECTED, {
    experienceLevel,
  });
};

// Billing Events
export const trackBillingPlanSelected = ({
  planName,
  billingInterval,
  seatCount,
  source,
}: {
  planName: string;
  billingInterval?: string;
  seatCount?: number;
  source: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PLAN_SELECTED, {
    planName,
    billingInterval,
    seatCount,
    source,
  });
};

export const trackBillingPlanConfigured = ({
  planName,
  billingInterval,
  seatCount,
  hasPaymentMethod,
}: {
  planName: string;
  billingInterval: string;
  seatCount: number;
  hasPaymentMethod: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PLAN_CONFIGURED, {
    planName,
    billingInterval,
    seatCount,
    hasPaymentMethod,
  });
};

export const trackBillingEnterpriseInquiryClicked = ({
  source,
}: {
  source: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_ENTERPRISE_INQUIRY_CLICKED, {
    source,
  });
};

export const trackEnterpriseInquirySubmitted = ({
  companyName,
  companySize,
  industry,
  jobTitle,
  techStack,
  enterpriseNeeds,
}: {
  companyName: string;
  companySize: string;
  industry: string;
  jobTitle: string;
  techStack: string[];
  enterpriseNeeds: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ENTERPRISE_INQUIRY_SUBMITTED, {
    companyName,
    companySize,
    industry,
    jobTitle,
    techStack,
    enterpriseNeeds,
  });

  window.dataLayer.push({
    event: "contact_sales",
  });
};

export const trackBillingCheckoutSubmitted = ({
  planName,
  billingInterval,
  seatCount,
  isPaymentMethodUpdate,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  isPaymentMethodUpdate: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_CHECKOUT_SUBMITTED, {
    planName,
    billingInterval,
    seatCount,
    isPaymentMethodUpdate,
  });
};

export const trackBillingCheckoutCompleted = ({
  planName,
  billingInterval,
  seatCount,
  isPaymentMethodUpdate,
  wasTrialing,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  isPaymentMethodUpdate: boolean;
  wasTrialing: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_CHECKOUT_COMPLETED, {
    planName,
    billingInterval,
    seatCount,
    isPaymentMethodUpdate,
    wasTrialing,
  });

  if (!isPaymentMethodUpdate) {
    window.dataLayer.push({
      event: "subscription_start",
    });
  }
};

export const trackBillingCheckoutFailed = ({
  planName,
  billingInterval,
  seatCount,
  error,
  isPaymentMethodUpdate,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  error: string;
  isPaymentMethodUpdate: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_CHECKOUT_FAILED, {
    planName,
    billingInterval,
    seatCount,
    error,
    isPaymentMethodUpdate,
  });
};

export const trackBillingSubscriptionCancelled = ({
  planName,
  billingInterval,
  seatCount,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_SUBSCRIPTION_CANCELLED, {
    planName,
    billingInterval,
    seatCount,
  });
};

export const trackBillingSubscriptionReactivated = ({
  planName,
  billingInterval,
  seatCount,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_SUBSCRIPTION_REACTIVATED, {
    planName,
    billingInterval,
    seatCount,
  });
};

export const trackBillingPaymentMethodUpdateStarted = ({
  source,
  currentPlan,
}: {
  source: string;
  currentPlan?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PAYMENT_METHOD_UPDATE_STARTED, {
    source,
    currentPlan,
  });
};

export const trackBillingBudgetUpdated = ({
  budgetAmount,
  isUnlimited,
  planName,
}: {
  budgetAmount?: number;
  isUnlimited: boolean;
  planName?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_BUDGET_UPDATED, {
    budgetAmount,
    isUnlimited,
    planName,
  });
};

export const trackBillingEmailUpdated = ({
  planName,
}: {
  planName?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_EMAIL_UPDATED, {
    planName,
  });
};

export const trackBillingInvoiceDownloaded = ({
  invoiceId,
  invoiceAmount,
  invoiceStatus,
}: {
  invoiceId: string;
  invoiceAmount: number;
  invoiceStatus: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_INVOICE_DOWNLOADED, {
    invoiceId,
    invoiceAmount,
    invoiceStatus,
  });
};

export const trackBillingInvoiceViewed = ({
  invoiceId,
  invoiceAmount,
  invoiceStatus,
}: {
  invoiceId: string;
  invoiceAmount: number;
  invoiceStatus: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_INVOICE_VIEWED, {
    invoiceId,
    invoiceAmount,
    invoiceStatus,
  });
};

export const trackBillingTrialPaymentPromptClicked = ({
  promptType,
  daysUntilTrialEnd,
  usageReason,
}: {
  promptType: string;
  daysUntilTrialEnd?: number;
  usageReason?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_TRIAL_PAYMENT_PROMPT_CLICKED, {
    promptType,
    daysUntilTrialEnd,
    usageReason,
  });
};

export const trackBillingUsageLimitActionClicked = ({
  limitReason,
  actionType,
}: {
  limitReason: string;
  actionType: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_USAGE_LIMIT_ACTION_CLICKED, {
    limitReason,
    actionType,
  });
};

export const trackBillingManageClicked = ({
  source,
}: {
  source: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_MANAGE_CLICKED, {
    source,
  });
};

export const trackBillingPlanChangeClicked = ({
  currentPlan,
  source,
}: {
  currentPlan?: string;
  source: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PLAN_CHANGE_CLICKED, {
    currentPlan,
    source,
  });
};

export const trackSessionStart = ({ userId }: { userId: string }): void => {
  if (config.mixpanel.enabled) {
    window.dataLayer.push({
      event: "session_start",
      user_id: userId,
    });
  }
};

// Onboarding Events
export const trackOnboardingEmailConnected = ({
  platform,
}: {
  platform: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_EMAIL_CONNECTED, {
    platform,
  });
};

export const trackOnboardingAppsSelected = ({
  apps,
}: {
  apps: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_APPS_SELECTED, {
    apps: apps.join(","),
    appCount: apps.length,
  });
};

export const trackOnboardingBookDemoClicked = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_BOOK_DEMO_CLICKED, {});
};

export const trackOnboardingSkipEmail = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_SKIP_EMAIL, {});
};

export default {
  trackAppInstalled,
  trackLogin,
  trackLogout,
  trackRelationshipCreated,
  trackRelationshipDeleted,
  trackRelationshipUpdated,
  trackHomeMessageSent,
  trackNewChatCreated,
  trackChatMessageSent,
  trackGroupCreated,
  trackGroupDeleted,
  trackGroupMemberAdded,
  trackGroupMemberRemoved,
  trackGroupOpened,
  trackGroupUpdated,
  trackLightDarkModeToggle,
  trackMessageCopied,
  trackMessageFeedbackSubmitted,
  trackToolFeedbackSubmitted,
  trackMessageThumbsDown,
  trackMessageThumbsUp,
  trackOrganizationConnectionAuthorized,
  trackOrganizationConnectionDeleted,
  trackOrganizationCreated,
  trackTemplateSignupStarted,
  trackTemplatePromptShown,
  trackTemplatePromptDismissed,
  trackTemplateCopyCompleted,
  trackTemplateCopyFailed,
  trackSkillCreated,
  trackSkillDeleted,
  trackSkillEdited,
  trackSlackOrganizationConnected,
  trackSlackUserLinked,
  trackUserConnectionAuthorized,
  trackUserConnectionDeleted,
  trackUserConnectionPromptClicked,
  trackDiscoverableConnectionAccessRequested,
  trackUserDeleted,
  trackUserInvited,
  trackUserProfileEdited,
  trackUserRoleEdited,
  trackFollowUpClicked,
  trackSaveIntegrationConnectionInstructions,
  trackOnboardingCompleted,
  trackOnboardingExperienceLevelSelected,
  trackIntegrationConnectionRecordSelected,
  trackSkillSelected,
  trackAudioRecordingStarted,
  trackAudioRecordingStopped,
  // Billing Events
  trackBillingPlanSelected,
  trackBillingPlanConfigured,
  trackBillingEnterpriseInquiryClicked,
  trackEnterpriseInquirySubmitted,
  trackBillingCheckoutSubmitted,
  trackBillingCheckoutCompleted,
  trackBillingCheckoutFailed,
  trackBillingSubscriptionCancelled,
  trackBillingSubscriptionReactivated,
  trackBillingPaymentMethodUpdateStarted,
  trackBillingBudgetUpdated,
  trackBillingEmailUpdated,
  trackBillingInvoiceDownloaded,
  trackBillingInvoiceViewed,
  trackBillingTrialPaymentPromptClicked,
  trackBillingUsageLimitActionClicked,
  trackBillingManageClicked,
  trackBillingPlanChangeClicked,
  trackSessionStart,
  // Onboarding
  trackOnboardingEmailConnected,
  trackOnboardingAppsSelected,
  trackOnboardingBookDemoClicked,
  trackOnboardingSkipEmail,
  // Global Search
};
